import axios from 'axios';
import { appInsightTrackEvent } from '../../../root/AppInsights';
import { getDpopKey } from './dpop-key-store';
import { createDpopProof } from './dpop-proof';

/**
 * Holds the gateway's DPoP-bound access token, obtained by trading the MSAL (B2C) token at
 * `POST /api/dpop/exchange`.
 *
 * FAILURE IS NOT FATAL. While DPoP is opt-in the gateway still accepts the B2C token as Bearer, so any
 * exchange failure - gateway flag off, network, an unexpected response - returns null and the caller
 * sends Bearer exactly as before this module existed. After a failure the exchange is not retried for
 * BYPASS_MS, so a gateway without DPoP costs one failed call per five minutes rather than one per
 * request, and nobody is signed out.
 *
 * Memory only. The token is short-lived (10 minutes) and is useless without the key, so there is
 * nothing to gain from persisting it and a reload simply exchanges again.
 *
 * RE-BIND. When the caller supplies `refreshB2cToken`, two answers from the exchange are met once with
 * a B2C token fetched fresh from the network before falling back:
 *  - 400 invalid_grant: the gateway binds a B2C token to the first key it is exchanged with, and this
 *    browser has since lost that key (site data cleared, storage unavailable so the key lived only in
 *    memory) while MSAL still holds the same B2C token. A new token means a new binding.
 *  - 401: the gateway rejected the B2C token itself - typically expired. The request interceptor sends
 *    the locally saved token and relies on a 401 *response* to refresh it; the exchange happens before
 *    any response, so without this it would fail and (with dev's 5-minute tokens, often) leave DPoP
 *    off for the whole back-off - or, in strict mode, fail every request until something else refreshed
 *    the token.
 */

const REFRESH_MARGIN_MS = 60_000;
const BYPASS_MS = 5 * 60_000;
const EXCHANGE_TIMEOUT_MS = 15_000;

interface BoundToken {
    token: string;
    expiresAt: number;
}

const tokens = new Map<string, BoundToken>();
const pending = new Map<string, Promise<string | null>>();
let bypassUntil = 0;

/**
 * A DPoP-bound token for this gateway origin, exchanging `b2cAccessToken` when there is none or it is
 * within a minute of expiry. Concurrent callers share one exchange. Null means "send Bearer".
 */
export function getDpopToken(
    gatewayOrigin: string,
    b2cAccessToken: string,
    refreshB2cToken?: () => Promise<string | null>,
    bypassMs: number = BYPASS_MS,
): Promise<string | null> {
    if (Date.now() < bypassUntil) return Promise.resolve(null);

    const current = tokens.get(gatewayOrigin);
    if (current && current.expiresAt - REFRESH_MARGIN_MS > Date.now()) {
        return Promise.resolve(current.token);
    }

    let exchange = pending.get(gatewayOrigin);
    if (!exchange) {
        exchange = exchangeToken(gatewayOrigin, b2cAccessToken, refreshB2cToken, bypassMs).finally(() => pending.delete(gatewayOrigin));
        pending.set(gatewayOrigin, exchange);
    }
    return exchange;
}

/** Drop the held token so the next request exchanges again - used when the gateway rejected it. */
export function invalidateDpopToken(): void {
    tokens.clear();
}

/** Forget everything, including a failure back-off. Used on sign-out. */
export function resetDpopSession(): void {
    tokens.clear();
    pending.clear();
    bypassUntil = 0;
}

async function exchangeToken(
    gatewayOrigin: string,
    b2cAccessToken: string,
    refreshB2cToken: (() => Promise<string | null>) | undefined,
    bypassMs: number,
): Promise<string | null> {
    try {
        return await requestExchange(gatewayOrigin, b2cAccessToken);
    } catch (error) {
        let failure = error;
        if (refreshB2cToken && (isInvalidGrant(error) || isRejectedB2cToken(error))) {
            try {
                const fresh = await refreshB2cToken();
                // Once only, and only with a token that differs: the same token would be refused again.
                if (fresh && fresh !== b2cAccessToken) return await requestExchange(gatewayOrigin, fresh);
            } catch (retryError) {
                failure = retryError;
            }
        }
        bypassUntil = Date.now() + bypassMs;
        reportFailure(failure);
        return null;
    }
}

/** 401 from the exchange: the B2C token it was handed is not accepted (expired, typically). */
function isRejectedB2cToken(error: unknown): boolean {
    return (error as { response?: { status?: number } })?.response?.status === 401;
}

function isInvalidGrant(error: unknown): boolean {
    const response = (error as { response?: { status?: number; data?: { error?: unknown } } })?.response;
    return response?.status === 400 && response.data?.error === 'invalid_grant';
}

async function requestExchange(gatewayOrigin: string, b2cAccessToken: string): Promise<string> {
    const key = await getDpopKey();
    const url = `${gatewayOrigin}/api/dpop/exchange`;
    const proof = await createDpopProof(key, 'POST', url);

    // Plain axios, not HttpService: this call must not re-enter the request interceptor that is
    // asking for the token in the first place.
    const response = await axios.post(url, null, {
        headers: { Authorization: `Bearer ${b2cAccessToken}`, DPoP: proof },
        timeout: EXCHANGE_TIMEOUT_MS,
    });

    const { access_token: token, token_type: tokenType, expires_in: expiresIn } = response.data ?? {};
    if (tokenType !== 'DPoP' || typeof token !== 'string' || typeof expiresIn !== 'number') {
        throw new Error('Unexpected DPoP exchange response');
    }

    tokens.set(gatewayOrigin, { token, expiresAt: Date.now() + expiresIn * 1000 });
    return token;
}

function reportFailure(error: unknown): void {
    try {
        const status = (error as { response?: { status?: number } })?.response?.status;
        const message = error instanceof Error ? error.message : String(error);
        console.warn('DPoP exchange failed; using Bearer for the next 5 minutes.', status ?? '', message);
        appInsightTrackEvent(
            [
                { name: 'Status', value: status ?? 'none' },
                { name: 'Error', value: message },
            ],
            'UI: dpop-exchange-failed',
        );
    } catch {
        // Telemetry must never turn a recoverable fallback into a failed request.
    }
}
